import { v1 } from "backoffice-api"
import { invalidateAllQueries } from "bonzai"
import { t } from "i18next"
import { Button, Checkbox, ErrorFallback, Input } from "materia"
import { forwardRef, useId, type InputHTMLAttributes } from "react"
import {
  useForm,
  type FormState,
  type UseFormRegister,
  type UseFormReset,
  type UseFormSetError,
} from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useCurrentCompanyContext } from "../../../components/AppShell/CurrentCompanyProvider"
import { useCurrentUserContext } from "../../../components/AppShell/CurrentUserProvider"
import { graphqlRequest } from "../../../graphql"
import { useIsFeatureFlagEnabled } from "../../../tools/useIsFeatureFlagEnabled"
import { sendVuplexMessage } from "../../../vuplex/sendVuplexMessage"
import { UserProfileView } from "../UserProfileView"
import { userUpdateMutation } from "./gql"
import type { FormDataType } from "./types"

import s from "./UserProfileSettings.module.scss"

export const UserProfileSettingsConnected = () => {
  const {
    currentCompanyError,
    isCurrentCompanyPending,
    currentCompany: { userDisplayNameFormat },
  } = useCurrentCompanyContext()
  const { getIsCompanyFlagEnabled } = useIsFeatureFlagEnabled()
  const {
    currentUserError,
    isCurrentUserPending,
    currentUser: { anonymous, email, phoneNumber, username, managedFields },
  } = useCurrentUserContext()

  const isPending = isCurrentUserPending || isCurrentCompanyPending
  const errors = [currentUserError, currentCompanyError].filter(Boolean)

  if (isPending) return <UserProfileSettingsSkeleton />
  if (errors.length > 0) return <UserProfileSettingsError errors={errors} />

  const isUsernameEditEnabled = !managedFields.includes("username")
  const isPhoneNumberEditEnabled = !managedFields.includes("phone_number")

  const isAnonymousEnabled = getIsCompanyFlagEnabled("anonymous")
  const isUsernameEnabled = userDisplayNameFormat === "username"

  return (
    <UserProfileSettings
      settings={{
        email: email ?? "",
        phoneNumber: phoneNumber ?? "",
        userName: username ?? "",
        showUserOnLeaderboard: !anonymous,
        isUsernameEnabled,
        isUsernameEditEnabled,
        isPhoneNumberEditEnabled,
        isAnonymousEnabled,
      }}
    />
  )
}

type UserProfileSettingsProps = {
  settings: {
    email: string
    phoneNumber: string
    userName: string
    isUsernameEnabled: boolean
    isUsernameEditEnabled: boolean
    isPhoneNumberEditEnabled: boolean
    isAnonymousEnabled: boolean
    showUserOnLeaderboard: boolean
  }
}
export const UserProfileSettings = ({
  settings: {
    email,
    phoneNumber,
    userName,
    isUsernameEnabled,
    isUsernameEditEnabled,
    isPhoneNumberEditEnabled,
    isAnonymousEnabled,
    showUserOnLeaderboard,
  },
}: UserProfileSettingsProps) => {
  const { t } = useTranslation()

  const defaultValues: FormDataType = {
    avatarId: null,
    userName,
    phoneNumber,
    showUserOnLeaderboard,
  }
  const form = useForm({ defaultValues })
  const { register, formState, handleSubmit, setError, reset } = form
  const { isSubmitting, isDirty, dirtyFields } = formState

  const onSubmit = useOnSubmit({ setError, reset, dirtyFields })

  return (
    <UserProfileView.Section title={t("userProfile.SECTION_PROFILE")}>
      <form className={s.userSettingsProfile} onSubmit={handleSubmit(onSubmit)}>
        {isAnonymousEnabled && (
          <Checkbox
            label={t("userProfile.NOT_ANONYMOUS")}
            {...register("showUserOnLeaderboard")}
          />
        )}

        {isUsernameEnabled && (
          <Input
            label={t("userProfile.USERNAME")}
            errorMessage={formState.errors.userName?.message}
            {...register("userName", {
              disabled: !isUsernameEditEnabled,
              validate: validateUserName,
            })}
          />
        )}

        <Input label={t("userProfile.MAIL")} disabled value={email ?? ""} />

        <Input
          label={t("userProfile.MOBILE")}
          errorMessage={formState.errors.phoneNumber?.message}
          {...register("phoneNumber", {
            disabled: !isPhoneNumberEditEnabled,
            validate: validatePhoneNumber,
          })}
        />

        <AvatarList register={register} />

        <Button type="submit" disabled={isSubmitting || !isDirty}>
          {t("userProfile.SAVE")}
        </Button>

        <p className={s.userSettingsProfile__error}>
          {formState.errors.root?.message}
        </p>
      </form>
    </UserProfileView.Section>
  )
}

export const UserProfileSettingsSkeleton = () => {
  const { t } = useTranslation()

  return (
    <UserProfileView.Section title={t("userProfile.SECTION_PROFILE")}>
      <div className={s.skeleton__setting} />
      <div className={s.skeleton__setting} />
      <div className={s.skeleton__setting} />
    </UserProfileView.Section>
  )
}

export const UserProfileSettingsError = ({ errors }: { errors: unknown[] }) => {
  const { t } = useTranslation()

  return (
    <UserProfileView.Section title={t("userProfile.SECTION_PROFILE")}>
      <ErrorFallback
        error={errors}
        title={t("error.GENERIC")}
        message={t("error.COULD_NOT_LOAD")}
      />
    </UserProfileView.Section>
  )
}

// TODO Move this to a separate file :(
type AvatarListProps = { register: UseFormRegister<FormDataType> }
const AvatarList = ({ register }: AvatarListProps) => {
  const { t } = useTranslation()
  const labelId = useId()
  const { data: avatars = [] } = useAvatars()

  const avatarElements = avatars.map((avatar) => (
    <Avatar
      key={avatar.id}
      src={avatar.url}
      value={avatar.id}
      alt={avatar.title}
      {...register("avatarId")}
    />
  ))

  return (
    <div className={s.avatars}>
      <span className={s.avatars__label} id={labelId}>
        {t("userProfile.CHOOSE_AVATAR")}
      </span>
      <ul className={s.avatars__avatars} aria-labelledby={labelId}>
        {avatarElements}
      </ul>
    </div>
  )
}

type AvatarProps = InputHTMLAttributes<HTMLInputElement> & {
  src: string
  alt: string
}
const Avatar = forwardRef<HTMLInputElement, AvatarProps>(
  ({ src, alt, ...props }, ref) => {
    const id = useId()

    return (
      <li className={s.avatar}>
        <input
          type="radio"
          id={id}
          className={s.avatar__input}
          ref={ref}
          {...props}
        />
        <label className={s.avatar__label} htmlFor={id}>
          <img className={s.avatar__image} src={src} alt={alt} loading="lazy" />
        </label>
      </li>
    )
  }
)

// Global allowlist of selectable avatar categories, matched on category id.
// Ids: 8 = Animals, 9 = Business, 10 = Humans. See FAME-704.
const ALLOWED_AVATAR_CATEGORY_IDS = [8, 9, 10]

// TODO [WEB-16831] move to GraphQL
const useAvatars = () => {
  return v1.getAvatars.useQuery([], {
    select: (res) =>
      res
        .filter((group) => ALLOWED_AVATAR_CATEGORY_IDS.includes(group.id))
        .flatMap((group) => group.avatars),
  })
}

const useOnSubmit = ({
  setError,
  reset,
  dirtyFields,
}: {
  setError: UseFormSetError<FormDataType>
  reset: UseFormReset<FormDataType>
  dirtyFields: FormState<FormDataType>["dirtyFields"]
}) => {
  return async (data: FormDataType) => {
    // Send only what the user changed. The backend rejects fields it considers
    // locked even when the value is unchanged, so an untouched field in the
    // input fails the whole mutation.
    const input = {
      ...(dirtyFields.showUserOnLeaderboard && {
        anonymous: !data.showUserOnLeaderboard,
      }),
      ...(dirtyFields.userName && { username: data.userName }),
      ...(dirtyFields.phoneNumber && { phoneNumber: data.phoneNumber }),
      ...(dirtyFields.avatarId && { avatarId: data.avatarId }),
    }

    try {
      await graphqlRequest({
        query: userUpdateMutation,
        variables: { input },
      })

      sendVuplexMessage({ type: "USER_SETTINGS_UPDATE" })
      reset(data)
      invalidateAllQueries()
    } catch {
      setError("root", { message: t("error.COULD_NOT_SAVE") })
    }
  }
}

// TODO [WEB-16831] move to GraphQL
const validateUserName = async (userName: string | null) => {
  if (userName === null) return

  const me = await v1.getMe.fetchQueryOnce([])

  if (me.username === userName) return

  if (userName === "") return t("onboarding.USERNAME_EXISTS")

  const { available } = await v1.getIsUsernameAvailable(userName)
  if (!available) return t("onboarding.USERNAME_EXISTS")
}

const validatePhoneNumber = (phoneNumber: string) => {
  const isValid = phoneNumber.startsWith("+") || phoneNumber.startsWith("00")
  if (!isValid) return t("userProfile.INVALID_PHONE_NUMBER")
}
